import React from 'react';
import { BotIcon, TrendingUpIcon, MegaphoneIcon, BarChart3Icon, MessageSquareIcon, WorkflowIcon } from 'lucide-react';
export function Services() {
  const consultingServices = [{
    icon: <WorkflowIcon size={28} className="text-blue-600" data-id="element-211" />,
    title: 'Process & Workflow Automation',
    description: 'Eliminate manual workflows and repetitive tasks. We map your operations and deploy AI agents that work 24/7, reducing costs and freeing your team for high-value work.',
    useCases: ['Workflow Automation', 'Document AI', 'Contract Management', 'Agentic AI', 'Multi-step Automation']
  }, {
    icon: <TrendingUpIcon size={28} className="text-blue-600" data-id="element-212" />,
    title: 'Sales & Revenue Acceleration',
    description: 'AI-powered lead scoring, pipeline forecasting, and outreach personalization that helps your sales team close more deals, faster — with less manual effort.',
    useCases: ['SDR Automation', 'CRM Optimization', 'AI-Enhanced CRM', 'Pipeline Forecasting']
  }, {
    icon: <MegaphoneIcon size={28} className="text-blue-600" data-id="element-213" />,
    title: 'Marketing Intelligence & Automation',
    description: 'Deploy AI to generate content at scale, automate campaign execution, and personalize customer journeys — without adding headcount.',
    useCases: ['Content Generation', 'Campaign Automation', 'Personalization at Scale', 'Performance Optimization']
  }, {
    icon: <MessageSquareIcon size={28} className="text-blue-600" data-id="element-214" />,
    title: 'Customer Engagement AI',
    description: 'Intelligent chatbots and voice agents that handle support, qualify leads, and guide onboarding — improving satisfaction while cutting response times and costs.',
    useCases: ['AI Chatbots', 'Voice Agents', 'Support Automation', 'AI-Driven Onboarding']
  }, {
    icon: <BarChart3Icon size={28} className="text-blue-600" data-id="element-215" />,
    title: 'Agentic AI & Multi-step Automation',
    description: 'Deploy autonomous AI agents that plan, reason, and execute complex multi-step tasks end-to-end — from data gathering to decision-making — without constant human oversight.',
    useCases: ['Agentic Workflows', 'Multi-step Automation', 'Autonomous Agents', 'AI Orchestration']
  }, {
    icon: <BotIcon size={28} className="text-blue-600" data-id="element-216" />,
    title: 'AI Stack Integration',
    description: 'Connect AI to your existing tools — CRM, ERP, marketing platforms. We handle the technical complexity so your team just sees results.',
    useCases: ['CRM Integration', 'ERP Connectivity', 'Multi-system Automation', 'API Orchestration']
  }];
  return <section id="services" className="py-20 bg-gray-50" data-id="element-217">
      <div className="container mx-auto px-4" data-id="element-218">
        <div className="text-center mb-16" data-id="element-219">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-220">
            AI Services Built for Growth
          </h2>
          <div className="w-20 h-1 bg-blue-600 mx-auto mb-6" data-id="element-221"></div>
          <p className="max-w-2xl mx-auto text-gray-600 text-lg" data-id="element-222">
            We focus on streamlining operations and accelerating revenue for
            mid-market companies through strategic AI implementation.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20" data-id="element-223">
          {consultingServices.map((service, index) => <div key={index} className="bg-white p-8 rounded-xl border border-gray-100 shadow-sm flex flex-col" data-id="element-224">
              <div className="mb-5 p-3 bg-blue-50 rounded-lg w-fit" data-id="element-225">
                {service.icon}
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3" data-id="element-226">
                {service.title}
              </h3>
              <p className="text-gray-600 leading-relaxed text-sm mb-6 flex-grow" data-id="element-227">
                {service.description}
              </p>
              <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-100" data-id="element-228">
                {service.useCases.map((tag, tagIndex) => <span key={tagIndex} className="text-xs font-medium px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full border border-blue-100" data-id="element-229">
                    {tag}
                  </span>)}
              </div>
            </div>)}
        </div>

        <div className="bg-white p-8 md:p-12 rounded-2xl border border-gray-200 shadow-sm" data-id="element-230">
          <div className="flex flex-col md:flex-row items-center gap-12" data-id="element-231">
            <div className="md:w-1/2" data-id="element-232">
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4" data-id="element-233">
                Our Consulting Approach
              </h3>
              <p className="text-gray-600 mb-6 text-lg leading-relaxed" data-id="element-234">
                We don't just implement AI — we embed alongside your team, map
                your highest-impact opportunities, and deliver measurable
                results within 90 days.
              </p>
              <ul className="space-y-3 mb-8" data-id="element-235">
                <li className="flex items-center gap-3 text-gray-700" data-id="element-236">
                  <div className="w-2 h-2 bg-blue-600 rounded-full shrink-0" data-id="element-237"></div>
                  <span data-id="element-238">Strategic roadmap & opportunity mapping</span>
                </li>
                <li className="flex items-center gap-3 text-gray-700" data-id="element-239">
                  <div className="w-2 h-2 bg-blue-600 rounded-full shrink-0" data-id="element-240"></div>
                  <span data-id="element-241">Custom implementation & integration</span>
                </li>
                <li className="flex items-center gap-3 text-gray-700" data-id="element-242">
                  <div className="w-2 h-2 bg-blue-600 rounded-full shrink-0" data-id="element-243"></div>
                  <span data-id="element-244">Team training & change management</span>
                </li>
              </ul>
              <a href="#contact" className="text-blue-600 font-semibold hover:text-blue-700 inline-flex items-center gap-2" data-id="element-245">
                Discuss your needs <span aria-hidden="true" data-id="element-246">&rarr;</span>
              </a>
            </div>
            <div className="md:w-1/2" data-id="element-247">
              <img src="https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80" alt="Strategic consulting session" className="rounded-xl shadow-lg w-full object-cover h-64 md:h-auto" data-id="element-248" />
            </div>
          </div>
        </div>
      </div>
    </section>;
}